import React, { useState, useEffect, useCallback } from 'react';
import JoinRitualsModal from './JoinRitualsModal';

const STORAGE_KEY = 'melanua_popup_last_shown';
const DISMISSED_KEY = 'melanua_popup_dismissed';

/**
 * AutoPopup Component
 * Automatically shows the subscription modal based on CMS settings:
 * - show_popup_on_visit: Whether to show automatically
 * - popup_delay_seconds: How long to wait before showing
 * - popup_frequency_hours: How often to show to the same visitor
 */
export default function AutoPopup() {
    const [isOpen, setIsOpen] = useState(false);
    const [settings, setSettings] = useState(null);
    const [hasChecked, setHasChecked] = useState(false);

    // Fetch popup settings from CMS
    useEffect(() => {
        fetch('/api/content/join-modal/')
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                setSettings(data);
                setHasChecked(true);
            })
            .catch(() => {
                setHasChecked(true);
            });
    }, []);

    // Check if we should show the popup
    const shouldShowPopup = useCallback(() => {
        if (!settings) return false;

        // Check if popup is enabled
        if (!settings.show_popup_on_visit) return false;

        // Check if user has permanently dismissed (e.g., after subscribing)
        const dismissed = sessionStorage.getItem(DISMISSED_KEY);
        if (dismissed === 'true') return false;

        // Check frequency - when was popup last shown?
        const lastShown = localStorage.getItem(STORAGE_KEY);
        if (lastShown) {
            const lastShownTime = parseInt(lastShown, 10);
            const frequencyHours = settings.popup_frequency_hours || 24;
            const frequencyMs = frequencyHours * 60 * 60 * 1000;
            const now = Date.now();

            if (now - lastShownTime < frequencyMs) {
                return false; // Not enough time has passed
            }
        }

        return true;
    }, [settings]);

    // Schedule popup display
    useEffect(() => {
        if (!hasChecked || !settings) return;

        if (!shouldShowPopup()) return;

        const delaySeconds = settings.popup_delay_seconds || 5;
        const delayMs = delaySeconds * 1000;

        const timer = setTimeout(() => {
            setIsOpen(true);
            // Record that we showed the popup
            localStorage.setItem(STORAGE_KEY, Date.now().toString());
        }, delayMs);

        return () => clearTimeout(timer);
    }, [hasChecked, settings, shouldShowPopup]);

    const handleClose = () => {
        setIsOpen(false);
    };

    // Don't render anything until we've checked settings
    if (!hasChecked) return null;

    return (
        <JoinRitualsModal
            isOpen={isOpen}
            onClose={handleClose}
        />
    );
}

/**
 * Mark popup as dismissed for the current session
 * Call this after a successful subscription to prevent showing again
 */
export function dismissPopupForSession() {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
}

/**
 * Reset popup state - for testing purposes
 */
export function resetPopupState() {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(DISMISSED_KEY);
}
